import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, merge, Observable, ReplaySubject, takeUntil } from 'rxjs';

import { IconService } from '../../services/icon.service';
import { getSidebarConfig, SidebarConfig } from '../../utils/sidebar.utils';
import { BaseComponent } from '../base-component/base.component';
import { SidebarService } from '../../services/sidebar.service';

export enum SourceApp {
  TEACHER = 'teacher',
  PAP = 'pap',
  CHILD_PORTFOLIO = 'child-portfolio',
  PARENT = 'parent',
  MANAGEMENT = 'management',
}

export interface SidebarItem {
  icon: string;
  selectedIcon?: string;
  label: string;
  url: string;
  selected: boolean;
  divider?: boolean;
  children?: SidebarItem[];
  isHref?: boolean;
  targetId?: string;
}

export interface SidebarGroup {
  label: string;
  items: SidebarItem[];
}

export interface MenuItem {
  url: string;
  icon: string;
  title: string;
  menuText: string;
  subtitle?: string;
  exactMatch?: boolean;
  divider?: boolean;
  /** Keep a colourful PNG icon in colour also when the item is not active (illustrated icons, e.g. Lodní deník). */
  colorIcon?: boolean;
  enrollmentState$?: Observable<boolean | null>;
  isHref?: boolean;
  locked?: boolean;
  subMenu?: Array<{
    url: string;
    title: string;
    menuText: string;
  }>;
  subItems?: Array<{
    url: string;
    title: string;
  }>;
  targetId?: string;
}

export interface MenuSection {
  sectionKey: string; // Unique identifier for the menu section
  title: string; // Display title for the section
  items: MenuItem[];
}

export enum MenuSectionKey {
  HOME = 'home',
  MANAGEMENT = 'management',
  CHILD_PORTFOLIO = 'child-portfolio',
  PAP = 'pap',
  KINDERGARTEN_ADMIN = 'settings',
  HELP = 'help',
}

export const URL_SECTION_MAPPING = {
  [MenuSectionKey.CHILD_PORTFOLIO]: ['/children-portfolio'],
  [MenuSectionKey.PAP]: ['/pap'],
  [MenuSectionKey.KINDERGARTEN_ADMIN]: ['/settings'],
  [MenuSectionKey.HELP]: ['/management/help'], // More specific pattern, checked first
  [MenuSectionKey.MANAGEMENT]: ['/management'], // General management pattern (removed root '/')
  [MenuSectionKey.HOME]: ['/home'], // General management pattern (removed root '/')
} as const;

export const SECTION_TITLES = {
  [MenuSectionKey.MANAGEMENT]: 'Management',
  [MenuSectionKey.CHILD_PORTFOLIO]: 'Portfolio dítěte',
  [MenuSectionKey.PAP]: 'Podpora pedagoga',
  [MenuSectionKey.KINDERGARTEN_ADMIN]: 'Správa školky',
  [MenuSectionKey.HELP]: 'Nápověda',
} as const;

@Component({
    selector: 'isophi-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SidebarComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sourceApp: SourceApp | null = null;

  @Input() menuSections: MenuSection[] = []; // Grouped menu sections

  @Input() firstLevelMenuItems: SidebarItem[] = []; // First-level menu items from parent

  @Input() onNavigationChange?: (url: string) => SidebarItem[]; // Callback to update first-level menu

  @Input() sidebarChangesTrigger$?: Observable<void> = new ReplaySubject<void>();

  @Input() homeLink?: string[] | null = null;

  @Input() showDemoSwitch = false;

  @Output() switchToDemo = new EventEmitter<void>();

  @Input() disabled = false;

  @Input() showMobileBurger = true;

  sidebarConfig: SidebarConfig | null = null;

  currentMenuItems: MenuItem[] = []; // Currently displayed second-level menu

  currentSectionTitle = '';

  readonly PNG_EXTENSION = '.png';

  isMobileMenuOpen = false;

  get hasSecondLevel(): boolean {
    return !!this.currentSectionTitle || this.currentMenuItems.length > 0;
  }

  private readonly router: Router = inject(Router);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected readonly sidebarService = inject(SidebarService);

  protected readonly icons = inject(IconService);

  ngOnInit(): void {
    if (!this.sourceApp) {
      throw new Error('Source app is required');
    }

    this.sidebarConfig = getSidebarConfig(this.sourceApp);
    this.updateMenus();

    // Listen to navigation and manuall trigger events to update selected states
    merge(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)), this.sidebarChangesTrigger$)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.updateMenus();
        this.cdr.markForCheck();
      });

    this.sidebarService.openMenu$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.toggleMobileMenu();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['menuSections'] || changes['firstLevelMenuItems']) && this.sidebarConfig) {
      this.updateMenus();
      this.cdr.markForCheck();
    }
  }

  private updateMenus(): void {
    const currentUrl = this.router?.url.split('?')[0] || window.location.pathname;

    // Update first-level menu items if callback is provided
    if (this.onNavigationChange) {
      this.firstLevelMenuItems = this.onNavigationChange(currentUrl);
    }

    // Update second-level menu
    this.updateSecondLevelMenu();
  }

  private updateSecondLevelMenu(): void {
    const currentUrl = this.router?.url.split('?')[0] || window.location.pathname;
    const activeSectionKey = this.getActiveSectionKey(currentUrl);

    // Find the corresponding menu section
    const activeSection = this.menuSections.find((section) => section.sectionKey === activeSectionKey);

    if (activeSection) {
      this.currentMenuItems = activeSection.items;
      this.currentSectionTitle = activeSection.title;
    } else {
      // No section found - show empty menu
      this.currentMenuItems = [];
      this.currentSectionTitle = '';
    }
  }

  private getActiveSectionKey(currentUrl: string): string {
    // Handle root URL case
    if (currentUrl === '/') {
      return MenuSectionKey.MANAGEMENT;
    }

    // Find the section that matches the current URL
    for (const [sectionKey, urlPatterns] of Object.entries(URL_SECTION_MAPPING)) {
      if (
        urlPatterns.some((pattern) => {
          const matches = currentUrl.startsWith(pattern);
          return matches;
        })
      ) {
        return sectionKey;
      }
    }

    // Default to management if no match found
    return MenuSectionKey.MANAGEMENT;
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.cdr.markForCheck();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.cdr.markForCheck();
  }

  onSecondLevelMenuItemClick(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  onSwitchToDemoClick(): void {
    this.switchToDemo.emit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
